/**
 * Pricing Estimation Hook
 *
 * React Query hook for real-time pricing estimation with debouncing and caching.
 * Provides automatic refetch on input changes, error handling, and stale-while-revalidate.
 *
 * Features:
 * - Debounced API calls (500ms delay)
 * - Automatic caching with React Query
 * - Stale-while-revalidate pattern
 * - Loading state management
 * - Error handling with retry logic
 * - Automatic cancellation on unmount
 *
 * @module hooks/usePricingEstimation
 *
 * @example
 * ```tsx
 * function ContainerStep() {
 *   const { pricing, isCalculating, error, refetch } = usePricingEstimation(
 *     'paso-rolloff',
 *     {
 *       containerSize: '20-yard',
 *       materialType: 'general-waste',
 *       selectedServices: [{ serviceId: 'fence', quantity: 1 }]
 *     },
 *     { enabled: true }
 *   );
 *
 *   if (isCalculating) return <LoadingSpinner />;
 *   if (error) return <ErrorMessage error={error} />;
 *   return <PricingDisplay pricing={pricing} />;
 * }
 * ```
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { pricingEstimationService } from '@/services/pricing-estimation.service';
import type { EstimatePriceRequest, EstimatePriceResponse } from '@/types/pricing-estimation';
import { ApiError } from '@/services/api';

/**
 * Hook options
 */
export interface UsePricingEstimationOptions {
  /** Whether to enable automatic fetching (default: true) */
  enabled?: boolean;
  /** Debounce delay in milliseconds (default: 500) */
  debounceMs?: number;
  /** Whether to retry on error (default: true) */
  retry?: boolean;
  /** Cache time in milliseconds (default: 5 minutes) */
  cacheTime?: number;
  /** Stale time in milliseconds (default: 30 seconds) */
  staleTime?: number;
}

/**
 * Hook return value
 */
export interface UsePricingEstimationResult {
  /** Current pricing estimate data */
  pricing: EstimatePriceResponse | undefined;
  /** Whether a calculation is in progress */
  isCalculating: boolean;
  /** Whether initial data is being loaded */
  isLoading: boolean;
  /** Error if calculation failed */
  error: ApiError | null;
  /** Manually trigger a refetch */
  refetch: () => Promise<void>;
  /** Whether there is cached data available */
  isFetched: boolean;
  /** Whether the query is currently fetching */
  isFetching: boolean;
}

/**
 * usePricingEstimation Hook
 *
 * Provides real-time pricing estimation with debouncing and caching.
 * Automatically refetches when parameters change after debounce delay.
 *
 * @param providerSlug - Provider slug for the API endpoint
 * @param params - Pricing estimation parameters
 * @param options - Hook configuration options
 * @returns Hook result with pricing data, loading states, and refetch function
 */
export function usePricingEstimation(
  providerSlug: string,
  params: EstimatePriceRequest | null,
  options: UsePricingEstimationOptions = {}
): UsePricingEstimationResult {
  const {
    enabled = true,
    debounceMs = 500,
    retry = true,
    cacheTime = 5 * 60 * 1000, // 5 minutes
    staleTime = 30 * 1000, // 30 seconds
  } = options;

  const queryClient = useQueryClient();
  const [debouncedParams, setDebouncedParams] = useState<EstimatePriceRequest | null>(params);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce the params changes
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedParams(params);
    }, debounceMs);

    // Cleanup on unmount or when params change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [params, debounceMs]);

  // Generate a stable query key based on provider and params
  const queryKey = [
    'pricing-estimation',
    providerSlug,
    debouncedParams?.containerSize,
    debouncedParams?.materialType,
    debouncedParams?.selectedServices,
    debouncedParams?.estimatedTonnage,
    debouncedParams?.hasTreatedWood,
    debouncedParams?.estimatedWeightTons,
    debouncedParams?.serviceAreaType,
    debouncedParams?.pricingModelId,
  ];

  // React Query for data fetching and caching
  const {
    data,
    isLoading,
    isFetching,
    isFetched,
    error: queryError,
    refetch: queryRefetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!debouncedParams) {
        throw new ApiError(
          'INVALID_PARAMS',
          'Missing required pricing parameters',
          400
        );
      }

      return pricingEstimationService.estimatePrice(providerSlug, debouncedParams);
    },
    enabled: enabled && !!debouncedParams && !!providerSlug,
    retry: retry ? 2 : false, // Retry twice on failure
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
    gcTime: cacheTime,
    staleTime,
    // Keep previous data while refetching (stale-while-revalidate)
    placeholderData: (previousData) => previousData,
  });

  // Convert React Query error to ApiError
  const error: ApiError | null = queryError instanceof ApiError
    ? queryError
    : queryError
    ? new ApiError('UNKNOWN_ERROR', String(queryError), 500)
    : null;

  // Manual refetch wrapper
  const refetch = async () => {
    await queryRefetch();
  };

  // Invalidate cache when provider changes
  useEffect(() => {
    return () => {
      // Cancel any in-flight requests on unmount
      queryClient.cancelQueries({ queryKey: ['pricing-estimation', providerSlug] });
    };
  }, [providerSlug, queryClient]);

  return {
    pricing: data,
    isCalculating: isFetching,
    isLoading,
    error,
    refetch,
    isFetched,
    isFetching,
  };
}
